import ArticleLayout from '@/pages/article/components/ArticleLayout';

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Ist OOPBUY besser als CNFans?', acceptedAnswer: { '@type': 'Answer', text: 'OOPBUY und CNFans haben unterschiedliche Stärken. OOPBUY punktet mit besserem Kundenservice und schnellerem QC, während CNFans oft günstigere Versandkosten bietet.' } },
    { '@type': 'Question', name: 'Welcher Shopping-Agent ist der beste für Deutschland?', acceptedAnswer: { '@type': 'Answer', text: 'Für den deutschen Markt sind OOPBUY, CNFans und Sugargoo die beliebtesten Optionen. OOPBUY bietet die beste deutschsprachige Unterstützung.' } },
    { '@type': 'Question', name: 'Gibt es eine Alternative zum OOPBUY Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Das OOPBUY Spreadsheet ist einzigartig in seiner umfassenden kuratierten Produktsammlung. Es gibt ähnliche Community-Listen, aber keine mit vergleichbarem Umfang.' } },
    { '@type': 'Question', name: 'Ist Sugargoo günstiger als OOPBUY?', acceptedAnswer: { '@type': 'Answer', text: 'Sugargoo hat oft niedrigere Servicegebühren, aber OOPBUY bietet mehr Features wie kostenlose QC-Bilder und ein besseres Spreadsheet.' } },
  ],
});

export default function VsAlternativesPage() {
  return (
    <ArticleLayout
      title="OOPBUY vs Alternatives 2026 – Der große Shopping-Agent Vergleich"
      description="CNFans, Sugargoo, Superbuy oder OOPBUY? Wir vergleichen alle großen chinesischen Shopping-Agenten in Sachen Preise, Versand, QC-Service und Benutzerfreundlichkeit für deutsche Käufer."
      heroImage="https://readdy.ai/api/search-image?query=Creative%20split%20composition%20photograph%20showing%20two%20contrasting%20shopping%20concepts%2C%20left%20side%20clean%20minimal%20white%20packaging%20with%20premium%20presentation%2C%20right%20side%20multiple%20colorful%20parcels%20and%20boxes%2C%20on%20warm%20terracotta%20toned%20surface%2C%20soft%20diffused%20studio%20lighting%2C%20artistic%20editorial%20still%20life%20with%20warm%20earthy%20palette&width=1200&height=500&seq=article-vs-hero&orientation=landscape"
      heroAlt="OOPBUY vs Alternatives Vergleich"
      breadcrumbLabel="vs Alternatives"
      schemaMarkup={FAQ_SCHEMA}
    >
      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          1. Der große Agenten-Vergleich 2026
        </h2>
        <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
          <p>
            Die Wahl des richtigen <strong>Shopping-Agenten</strong> ist entscheidend für dein Einkaufserlebnis aus China. In diesem Vergleich analysieren wir die vier größten Agenten – <strong>OOPBUY, CNFans, Sugargoo und Superbuy</strong> – und zeigen dir, welcher am besten zu deinen Bedürfnissen als deutscher Käufer passt.
          </p>
          <p>
            Jeder Agent hat seine Stärken und Schwächen. Während OOPBUY mit einem exzellenten <strong>Spreadsheet-System</strong> und gutem Kundenservice punktet, locken andere mit günstigeren Versandkosten oder einer größeren Auswahl an Zusatzservices. Der richtige Agent hängt von deinen Prioritäten ab.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          2. Vergleichstabelle: OOPBUY vs CNFans vs Sugargoo vs Superbuy
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-200">
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Feature</th>
                <th className="text-center py-3 px-3 font-medium text-accent-600">OOPBUY</th>
                <th className="text-center py-3 px-3 font-medium text-foreground-500">CNFans</th>
                <th className="text-center py-3 px-3 font-medium text-foreground-500">Sugargoo</th>
                <th className="text-center py-3 px-3 font-medium text-foreground-500">Superbuy</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Service-Gebühr', '5-8%', '3-5%', '4-6%', '5-10%'],
                ['QC-Bilder', 'Kostenlos (3-5)', 'Kostenlos (2-3)', 'Kostenlos (3)', 'Kostenlos (3-5)'],
                ['Spreadsheet', '★★★★★', '★★★☆☆', '★★★☆☆', '★★★★☆'],
                ['Deutsch Support', '★★★★☆', '★★☆☆☆', '★★☆☆☆', '★★★☆☆'],
                ['Versandoptionen', '10+ Linien', '8+ Linien', '12+ Linien', '15+ Linien'],
                ['Lagerzeit', '90 Tage gratis', '180 Tage gratis', '100 Tage gratis', '90 Tage gratis'],
                ['App-Qualität', '★★★★☆', '★★★★☆', '★★★☆☆', '★★★★★'],
                ['Website-UI', '★★★★☆', '★★★☆☆', '★★★★☆', '★★★★☆'],
              ].map((row, i) => (
                <tr key={i} className="border-b border-secondary-100">
                  <td className="py-3 pr-4 font-medium text-foreground-800">{row[0]}</td>
                  <td className="py-3 px-3 text-center text-accent-700 font-medium">{row[1]}</td>
                  <td className="py-3 px-3 text-center text-foreground-500">{row[2]}</td>
                  <td className="py-3 px-3 text-center text-foreground-500">{row[3]}</td>
                  <td className="py-3 px-3 text-center text-foreground-500">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          3. Stärken und Schwächen im Detail
        </h2>
        <div className="space-y-4">
          {[
            { name: 'OOPBUY', strengths: 'Bestes Spreadsheet-System, gute QC-Bilder, solider Kundenservice, deutschsprachige Community', weaknesses: 'Etwas höhere Servicegebühren, weniger Versandoptionen als Superbuy', verdict: 'Beste Wahl für Spreadsheet-Nutzer und Einsteiger' },
            { name: 'CNFans', strengths: 'Günstigste Gebühren, große Community, lange kostenlose Lagerzeit', weaknesses: 'Schwächerer deutscher Support, QC-Bilder manchmal spärlich', verdict: 'Gut für preisbewusste Vielkäufer' },
            { name: 'Sugargoo', strengths: 'Viele Versandoptionen, günstige Versandlinien, intuitive App', weaknesses: 'Mittelmäßige QC-Qualität, kein dediziertes Spreadsheet', verdict: 'Solide Allround-Wahl mit Versand-Fokus' },
            { name: 'Superbuy', strengths: 'Beste App, meiste Versandoptionen, längste Erfahrung am Markt', weaknesses: 'Höchste Gebühren, kein Spreadsheet-System, komplexe UI', verdict: 'Für erfahrene Käufer mit hohen Ansprüchen' },
          ].map((agent) => (
            <div key={agent.name} className="bg-background-100 rounded-xl p-6">
              <h3 className="text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{agent.name.charAt(0)}</span>
                </span>
                {agent.name}
                <span className="text-xs font-normal text-foreground-500 ml-auto">{agent.verdict}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-accent-600 font-medium mb-1">Stärken</p>
                  <p className="text-foreground-600 text-xs leading-relaxed">{agent.strengths}</p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-medium mb-1">Schwächen</p>
                  <p className="text-foreground-600 text-xs leading-relaxed">{agent.weaknesses}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          4. Unser Fazit für deutsche Käufer
        </h2>
        <div className="bg-background-100 rounded-xl p-6 text-sm md:text-base text-foreground-700 leading-relaxed">
          <p className="mb-3">
            Für die <strong>meisten deutschen Käufer</strong> ist OOPBUY die beste Wahl – besonders wenn du Wert auf das Spreadsheet-System, gute QC-Bilder und deutschsprachige Ressourcen legst. Die leicht höheren Gebühren werden durch die Zeitersparnis und die Sicherheit mehr als wettgemacht.
          </p>
          <p>
            Wenn du hauptsächlich auf den <strong>Preis</strong> achtest und bereit bist, etwas mehr selbst zu recherchieren, kann CNFans eine gute Alternative sein. Für Power-User mit speziellen Versandanforderungen ist Superbuy dank der größten Auswahl an Versandlinien interessant.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
          5. Häufige Fragen zum Agenten-Vergleich
        </h2>
        <div className="space-y-3">
          {[
            { q: 'Ist OOPBUY besser als CNFans?', a: 'OOPBUY und CNFans haben unterschiedliche Stärken. OOPBUY bietet ein überlegenes Spreadsheet-System, bessere QC-Bilder und mehr deutschsprachige Ressourcen. CNFans punktet mit niedrigeren Gebühren und längerer kostenloser Lagerzeit. Für Einsteiger und Spreadsheet-Nutzer ist OOPBUY die bessere Wahl.' },
            { q: 'Welcher Shopping-Agent ist der beste für Deutschland?', a: 'Für den deutschen Markt empfehlen wir OOPBUY aufgrund des besten Spreadsheet-Systems, der guten deutschsprachigen Unterstützung und der zuverlässigen QC-Bilder. CNFans ist eine gute Alternative für preisbewusste Käufer, Sugargoo für flexible Versandoptionen.' },
            { q: 'Gibt es eine Alternative zum OOPBUY Spreadsheet?', a: 'Das OOPBUY Spreadsheet ist in seiner umfassenden und kuratierten Form einzigartig. Es gibt Community-Listen für andere Agenten, aber keine erreicht den gleichen Umfang und die gleiche Qualität. Das Spreadsheet ist einer der Hauptgründe, warum viele Käufer OOPBUY bevorzugen.' },
            { q: 'Kann ich zwischen Agenten wechseln?', a: 'Ja, du bist an keinen Agenten gebunden. Viele erfahrene Käufer nutzen mehrere Agenten parallel – zum Beispiel OOPBUY für das Spreadsheet und CNFans für besonders günstige Bestellungen. Die Produktlinks von Taobao und Weidian funktionieren bei allen Agenten.' },
          ].map((faq, i) => (
            <details key={i} className="bg-background-100 rounded-xl group">
              <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                {faq.q}
                <i className="ri-add-line text-foreground-400 group-open:hidden" />
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
              </summary>
              <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}